import axios from 'axios'
import {findFood} from "@utils/foods";
import localforage from "localforage";
import {getSavedState, saveState} from "@utils/util";
import {FOODS_DRI, FOODS_LIST, FOOD_UPDATE_REQUIRED} from "@utils/storageKeys";


export const state = {
    _foods: null,
    favouriteFoods: getSavedState('food.favouriteFoods') || [],
    lastUpdated: getSavedState('food.lastUpdated'),
    _dris: null
};

export const getters = {

    foods(state) {
        return state._foods
    },
    dris(state){
        return state._dris
    },
    favouriteFoods(state){
        return state.favouriteFoods;
    }

};

export const mutations = {
    // Frozen so Vue doesn't track ~12k foods; changes replace the whole list.
    CACHE_FOODS(state, foods){
        state._foods = Object.freeze(foods);
    },

    CACHE_FAVOURITE_FOODS(state, foods){
        if(Array.isArray(foods)){
            state.favouriteFoods = [...foods];
        }else{
            state.favouriteFoods.push(foods);
        }
        saveState('food.favouriteFoods', state.favouriteFoods);
    },

    CACHE_DRIS(state, dris){
        state._dris = dris
    },

    CACHE_FOODS_TIME(state, time){
        state.lastUpdated = time;
    },


    ADD_NEW_FOOD(state, food){
        state._foods = Object.freeze([...state._foods, food]);
        return saveDatabaseState(FOODS_LIST, state._foods);
    },

    UPDATE_FOOD(state, food){
        state._foods = Object.freeze(state._foods.map(x => x.id === food.id ? food : x));
        return saveDatabaseState(FOODS_LIST, state._foods);
    },
    DELETE_FOOD(state, id) {
        state._foods = Object.freeze(state._foods.filter(x => x.id !== id));
        return saveDatabaseState(FOODS_LIST, state._foods);
    }
};

export const actions = {

    async init({ dispatch, rootGetters }) {
        if (!rootGetters['auth/loggedIn']) return;
        dispatch('cacheFavouriteFoods');
        dispatch('refreshCacheFoods');

        let dris = await localforage.getItem(FOODS_DRI);

        if(!dris){
            dispatch('fetchCacheDris');
        }else{
            dispatch('refreshCacheDris');
        }
    },


    // Moved from BaseMixin.fetchFoods — same semantics, incl. the one-shot
    // forced refetch after a version bump (food.foodUpdateRequired flag).
    async ensureFoods({dispatch}) {
        if (localStorage.getItem(FOOD_UPDATE_REQUIRED)) {
            await dispatch('fetchCacheFoods');
            localStorage.removeItem(FOOD_UPDATE_REQUIRED);
            return;
        }
        return dispatch('refreshCacheFoods');
    },

    addNewFood({commit}, payload){
       return commit('ADD_NEW_FOOD', payload);
    },

    updateFood({commit}, payload){
        return commit('UPDATE_FOOD', payload);
    },

    deleteFood({state, commit, dispatch}, id){
        return commit('DELETE_FOOD', id)
    },

    async fetchCacheFoods({ commit }){
        console.warn('Fetching foods...');

        return axios.get(process.env.VUE_APP_API + `/food/`).then(result => {
            const now = new Date();
            commit('CACHE_FOODS', result.data.rows);
            commit('CACHE_FOODS_TIME', now);
            saveState('food.lastUpdated', now);
            saveDatabaseState(FOODS_LIST, result.data.rows);

        }).catch( e => {
            console.error('Food fetching has failed!');
            return Promise.reject(e);
        });
    },

    // Swaps in a fresh copy of the user's own foods, keeping the cached system ones.
    async refreshUserFoods({state, commit}){
        const result = await axios.get(process.env.VUE_APP_API + `/food/`, {params: {skipSystem: true}});
        const foods = [...state._foods.filter(x => x.isSystem), ...result.data.rows];
        commit('CACHE_FOODS', foods);
        saveDatabaseState(FOODS_LIST, foods);
    },

    // The cached list can predate foods made elsewhere (another computer on the account, a new
    // recipe, the MCP). A miss refreshes the user's own foods, then the whole list if still missing.
    async ensureFoodIds({state, dispatch}, foodIds){
        const isMissing = id => !state._foods || !findFood(state._foods, id);
        if (!foodIds.some(isMissing)) return;

        if (state._foods) await dispatch('refreshUserFoods');
        if (foodIds.some(isMissing)) await dispatch('fetchCacheFoods');
    },

    async cacheFavouriteFoods({commit}){
        console.warn('caching favourite foods');
        return axios.get(process.env.VUE_APP_API + `/users/favourite-foods`).then(result => {
            commit('CACHE_FAVOURITE_FOODS', result.data || []);
        }).catch( e => {
            console.error('Favourite foods fetching has failed!');
            return Promise.reject(e);
        });
    },

    async updateFavouriteFoods({state, commit}, foodId){
        if(state.favouriteFoods.includes(foodId)){
            commit('CACHE_FAVOURITE_FOODS', state.favouriteFoods.filter(x => x !== foodId));
        }else{
            commit('CACHE_FAVOURITE_FOODS', foodId)
        }
        return axios.put(process.env.VUE_APP_API + `/users/favourite-foods`, {favouriteFoods: state.favouriteFoods}).then().catch( e => {
            console.error('Update of favourite foods failed');
            return Promise.reject(e);
        });
        //

    },

    async fetchCacheDris({commit}){
        return axios.get(process.env.VUE_APP_API + `/general/dris`).then(result => {
            commit('CACHE_DRIS', result.data);
            saveDatabaseState(FOODS_DRI, result.data);

        }).catch( e => {
            console.error('DRI fetching has failed!');
            return Promise.reject(e);
        });
    },

    async refreshCacheFoods({state, commit, dispatch}){
        if(!state.lastUpdated) commit('CACHE_FOODS_TIME', getSavedState('food.lastUpdated'));

        if(daysBetween(new Date(), new Date(state.lastUpdated)) >= 1){
            return dispatch('fetchCacheFoods');
        }
        if(state._foods && state._foods.length > 0) return;

        const foods = await getDatabaseState(FOODS_LIST);
        if(!foods || foods.length === 0){
            return dispatch('fetchCacheFoods');
        }
        // a network fetch may have landed while we were reading; don't overwrite it
        if(!state._foods || state._foods.length === 0){
            commit('CACHE_FOODS', foods);
        }
    },

    async refreshCacheDris({state, commit, dispatch}){
        let dris = await getDatabaseState(FOODS_DRI);
        if(daysBetween(new Date(), new Date(state.lastUpdated)) >= 1){
            return dispatch('fetchCacheDris');
        }
        commit('CACHE_DRIS', dris);
    },




    reset({commit}){
        commit('CACHE_FOODS', null);
        commit('CACHE_DRIS', null);
    }
};

function daysBetween(date1, date2) {

    // The number of milliseconds in one day
    const ONE_DAY = 1000 * 60 * 60 * 24;

    // Calculate the difference in milliseconds
    const differenceMs = Math.abs(date1 - date2);

    // Convert back to whole days and return
    return Math.floor(differenceMs / ONE_DAY);

}
// // LOCAL FORAGE HELPERS
//
function getDatabaseState(key){
    return localforage.getItem(key);
}
//
function saveDatabaseState(key, state){
    localforage.removeItem(key).then( res => {
        localforage.setItem(key, state).then(res => { return Promise.resolve(res)}).catch(err => {
            console.log(err)
            return Promise.reject(err)});
    });

}
